/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import {
  Button,
  ButtonVariety,
  Divider,
  Link,
  LinkHighlight,
  MessageInline,
  MessageVariety,
  Modal,
} from '@sonarsource/echoes-react';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SharedDocLink, useSharedDocUrl } from '~adapters/helpers/docs';
import { usePostAtlassianApplicationConfigurationMutation } from '../../queries';
import { AtlassianApplicationConfigurationResponse } from '../../types';
import { OrganizationJiraForm } from './OrganizationJiraForm';

interface OrganizationJiraAppEditModalProps {
  atlassianApplicationConfiguration: AtlassianApplicationConfigurationResponse;
  isPendingUpdate: boolean;
  onChangeIsFormOpen?: (isFormOpen: boolean) => void;
  onUpdatedAppConfiguration: () => void;
}

export function OrganizationJiraAppEditModal({
  atlassianApplicationConfiguration,
  isPendingUpdate,
  onChangeIsFormOpen,
  onUpdatedAppConfiguration,
}: Readonly<OrganizationJiraAppEditModalProps>) {
  const { formatMessage } = useIntl();
  const docUrl = useSharedDocUrl();

  const [isEditing, setIsEditing] = useState(false);
  const [jiraAppCredentials, setJiraAppCredentials] = useState({
    jiraAppClientId: '',
    jiraAppSecret: '',
  });

  const { mutate: postAtlassianApplicationConfiguration, isPending: isUpdatingAppConfiguration } =
    usePostAtlassianApplicationConfigurationMutation();

  const onUpdateAppConfiguration = () => {
    postAtlassianApplicationConfiguration(
      {
        clientId: jiraAppCredentials.jiraAppClientId,
        secret: jiraAppCredentials.jiraAppSecret,
      },
      {
        onSuccess: () => {
          onUpdatedAppConfiguration();
          closeEditForm();
        },
      },
    );
  };

  const onUpdateCredentials = useCallback(
    (jiraAppCredentials: { jiraAppClientId: string; jiraAppSecret: string }) => {
      setJiraAppCredentials(jiraAppCredentials);
    },
    [],
  );

  const closeEditForm = () => {
    setIsEditing(false);
    onChangeIsFormOpen?.(false);
  };
  const openEditForm = () => {
    setIsEditing(true);
    onChangeIsFormOpen?.(true);
  };

  return (
    <Modal
      content={
        <div className="sw-flex sw-flex-col sw-justify-between sw-mt-6 sw-gap-6">
          <FormattedMessage
            id="organization.jira.binding.app_configuration.helper_intro"
            tagName="p"
            values={{
              link: (text) => (
                <Link
                  enableOpenInNewTab
                  highlight={LinkHighlight.Accent}
                  to={docUrl(SharedDocLink.JiraIntegration)}
                >
                  {text}
                </Link>
              ),
            }}
          />
          <Divider />
          <OrganizationJiraForm
            initialClientId={atlassianApplicationConfiguration.clientId}
            onUpdateCredentials={onUpdateCredentials}
          />
        </div>
      }
      footerLink={
        <MessageInline variety={MessageVariety.Info}>
          <FormattedMessage id="organization.jira.binding.app_configuration.submit_note" />
        </MessageInline>
      }
      isOpen={isEditing}
      onOpenChange={setIsEditing}
      primaryButton={
        <Button
          aria-label={formatMessage({ id: 'update' })}
          isDisabled={
            isUpdatingAppConfiguration ||
            isPendingUpdate ||
            jiraAppCredentials.jiraAppClientId === '' ||
            jiraAppCredentials.jiraAppSecret === ''
          }
          isLoading={isUpdatingAppConfiguration || isPendingUpdate}
          name="update"
          onClick={onUpdateAppConfiguration}
          variety={ButtonVariety.Primary}
        >
          <FormattedMessage id="update" />
        </Button>
      }
      secondaryButton={
        <Button
          aria-label={formatMessage({ id: 'cancel' })}
          name="cancel"
          onClick={closeEditForm}
          variety={ButtonVariety.Default}
        >
          <FormattedMessage id="cancel" />
        </Button>
      }
      title={<FormattedMessage id="organization.jira.binding.app_configuration.edit_modal.title" />}
    >
      <Button
        aria-label={formatMessage({ id: 'edit' })}
        name="edit"
        onClick={openEditForm}
        variety={ButtonVariety.Default}
      >
        <FormattedMessage id="edit" />
      </Button>
    </Modal>
  );
}
